import React, { useState, useCallback, useMemo, useEffect, useRef, memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Form, useFormikContext } from "formik";
import * as R from "ramda";
import { Box, Heading, Text, Button, Stack, OrderedList, ListItem } from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { useHistory, useRouteMatch } from "react-router-dom";

import { localizedRegionsFactory, toRegionOptions, sortCountries } from "utils/regions";
import { useAnalytics } from "utils";
import Accordion, { AccordionSection } from "components/Accordion";
import {
  SearchableSelectField,
  TextFieldGroup,
  DOBSelectField,
  SSNField,
} from "components/Form";
import { useFlowConfig } from "flows/hooks";

import KYCFirstHalfAddressSection from "./KYCFirstHalfAddressSection";
import FormValidationErrorDebugView from "./FormValidationErrorDebugView";

import { EmailSection } from "./EmailSection";
import { NextButton } from "./Buttons";
import { useLanguageContext } from "utils/LanguageProvider";
import { TILIA_TOS_URL } from "env";

const sections = new Set(["country", "personal", "taxInfo", "contact"]);

function KYCFirstHalf({ onUpdateTouchedFields, onShowRetryErrMessage, isRetry, condition }) {
  const { locale } = useLanguageContext();
  const { formatMessage } = useIntl();
  const hasMounted = useRef(false);
  const { values, isSubmitting, handleSubmit, errors, setFieldValue, initialValues, touched } =
    useFormikContext();
  const history = useHistory();
  const { path } = useRouteMatch();
  const [activeSection, setActiveSection] = useState(0);
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const { verificationLevel } = useFlowConfig();
  const regions = sortCountries(localizedRegionsFactory(formatMessage));
  const regionsAsOptions = toRegionOptions(regions);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("KYC", "KYC_UserDataForm_Viewed", {
        verificationLevel,
        limitToBasicPII: initialValues.pii_level === "basic",
      });
      viewEventFired.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, viewEventFired]);

  // If the user fills out the second half of the form and then comes back to the first
  // half and changes a value that negates their requirement to fill out the second
  // half, then we clear out the values we already have for the second half, in order
  // to not send that data over.
  useEffect(() => {
    if (hasMounted.current && Boolean(values.documents.document_type)) {
      setFieldValue("documents", initialValues.documents);
    }
    hasMounted.current = true;
  }, [
    values.country,
    values.contact.state,
    values.documents.document_type,
    initialValues.documents,
    setFieldValue,
  ]);

  const goToNextSection = useCallback(() => {
    setActiveSection((currentSection) => currentSection + 1);
  }, []);

  // Here we check if the user needs to provide us with documents. We use the validation
  // of the 'documents' section of our schema to verify this so that both form validation
  // and form submission comes from a single source of truth.
  const maybeSubmit = useCallback(
    (event) => {
      event.preventDefault();

      const shouldGoToSecondHalf = errors["documents"] || Boolean(values.documents.document_type);
      if (shouldGoToSecondHalf) {
        // If the documents section of our schema has errors, that means the user needs to provide
        // us with additional documents, so we send them to the /documents section
        history.push(`${path}/documents`);
      } else {
        const valuesSameAsInitialValues = R.equals(values, initialValues);
        if (valuesSameAsInitialValues && isRetry) {
          if (onShowRetryErrMessage) onShowRetryErrMessage();
          return;
        }
        // If we don't have any validation errors in the documents section, then this user is
        // good to go and we pass the form submission handling over to Formik
        if (onUpdateTouchedFields) {
          // keep track of which fields were touched
          onUpdateTouchedFields(touched);
        }
        handleSubmit();
      }
    },
    [
      errors,
      handleSubmit,
      history,
      path,
      values,
      onUpdateTouchedFields,
      touched,
      initialValues,
      onShowRetryErrMessage,
      isRetry,
    ]
  );

  // We need to handle our own way of dealing with whether or not this first half of the form
  // is valid here. If a user needs to provide additional documentation, then Formik's isValid
  // would be returning false here, not allowing the user to press "Continue"
  const hasErrors = useMemo(() => Object.keys(errors).some((e) => sections.has(e)), [errors]);
  const isValid = !hasErrors;

  const selectedCountry = useMemo(
    () => R.find(R.propEq("ISO", values.country))(regions),
    [regions, values.country]
  );
  const isInUnitedStates = selectedCountry?.ISO === "US";

  return (
    <Form onSubmit={maybeSubmit} data-testid="kyc-first-part">
      <Box px={4} pb={6}>
        <Heading mb={2}>
          {condition === "TAX_UPDATE" ? (
            <FormattedMessage defaultMessage="Update your info" description="Page header" />
          ) : (
            <FormattedMessage defaultMessage="Verify your identity" description="Page header" />
          )}
        </Heading>
        <Text fontSize="sm" color="secondaryText">
          {condition === "TAX_UPDATE" ? (
            <FormattedMessage
              defaultMessage="We need a few things to prepare your tax forms this year. It should only take a moment."
              description="User message"
            />
          ) : (
            <FormattedMessage
              defaultMessage="We need to get to know you a bit better. Please fill out the form below, it should only take a few minutes."
              description="User message"
            />
          )}
        </Text>
      </Box>

      <Box>
        <Accordion activeSection={activeSection} setActiveSection={setActiveSection}>
          <AccordionSection
            title={formatMessage({
              defaultMessage: "Email",
              description: "Section title",
            })}
            section="email"
          >
            <EmailSection onComplete={goToNextSection} />
          </AccordionSection>

          <AccordionSection
            title={formatMessage({
              defaultMessage: "Country",
              description: "Section title",
            })}
            section="country"
            previousSection="email"
          >
            <Stack spacing={6}>
              <SearchableSelectField
                label={formatMessage({
                  defaultMessage: "Country/Region of residence",
                  description: "Dropdown label",
                })}
                name="country"
                options={regionsAsOptions}
                menuPortalTarget={document.body}
                placeholder={formatMessage({
                  defaultMessage: "Select...",
                  description: "select placeholder",
                })}
              />

              <NextButton section="country" onClick={goToNextSection} />
            </Stack>
          </AccordionSection>

          <AccordionSection
            title={formatMessage({
              defaultMessage: "Personal Info",
              description: "Section title",
            })}
            section="personal"
            previousSection="country"
          >
            <Stack spacing={6}>
              <Stack spacing={3}>
                <TextFieldGroup
                  label={formatMessage({
                    defaultMessage: "Full name",
                    description: "Input field label",
                  })}
                  placeholder={formatMessage({
                    defaultMessage: "First name",
                    description: "input label",
                  })}
                  name="personal.first"
                  aria-label="First name"
                />
                <TextFieldGroup
                  placeholder={formatMessage({
                    defaultMessage: "M.I. (optional)",
                    description:
                      "'M.I.' is an example of initials. Need to translate the word 'optional'.",
                  })}
                  name="personal.middle"
                  aria-label="Middle name"
                  isOptional
                />
                <TextFieldGroup
                  placeholder={formatMessage({
                    defaultMessage: "Last name",
                    description: "Input field label",
                  })}
                  name="personal.last"
                  aria-label="Last name"
                />
              </Stack>

              {condition !== "TAX_UPDATE" && (
                <DOBSelectField
                  minAgeValidationError={errors?.personal?.dob?.minAge}
                  locale={locale}
                />
              )}

              <NextButton section="personal" onClick={goToNextSection} />
            </Stack>
          </AccordionSection>

          <AccordionSection
            title={formatMessage({
              defaultMessage: "Tax Info",
              description: "Section title",
            })}
            section="taxInfo"
            previousSection="personal"
          >
            <Stack spacing={6}>
              {isInUnitedStates && condition !== "TAX_UPDATE" ? (
                <SSNField
                  name="taxInfo.ssn"
                  label={formatMessage({
                    defaultMessage: "Social security number",
                    description: "FIeld input label",
                  })}
                />
              ) : null}
              {isInUnitedStates ? (
                <Stack>
                  <Text fontSize="md" fontWeight="medium">
                    <FormattedMessage
                      defaultMessage="Under penalty of perjury, I certify that:"
                      description="Legal information"
                    />
                  </Text>
                  <OrderedList fontSize="sm">
                    <FormattedMessage
                      defaultMessage="<ListItem>The social security number provided above is my correct taxpayer identificiation number (TIN), and </ListItem><ListItem>I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding, and </ListItem><ListItem>I am a U.S. citizen or other U.S. person (including a U.S. resident alien)</ListItem>"
                      description="Legal information"
                      values={{
                        ListItem: (msg) => <ListItem>{msg}</ListItem>,
                      }}
                    />
                  </OrderedList>
                </Stack>
              ) : (
                <Stack>
                  <Text fontSize="md" fontWeight="medium">
                    <FormattedMessage
                      defaultMessage="Under penalties of perjury, I declare that:"
                      description="Legal information"
                    />
                  </Text>
                  <OrderedList fontSize="sm">
                    <FormattedMessage
                      defaultMessage={`
                        <ListItem>
                          The payee providing this certification is not a United States person (i.e., a citizen or resident of the United States as determined for U.S. federal tax purposes, a corporation or partnership created or organized in the United States or under the law of the United States or of any State, any estate that would be subject to U.S. federal income tax on income from sources without the United States which is not effectively connected with the conduct of a trade or business within the United States, or any trust if a court within the United States is able to exercise primary supervision over the administration of the trust and one or more United States persons have the authority to control all substantial decisions of the trust)
                        </ListItem>
                        <ListItem>
                          The income to which this certification relates is not effectively connected
                          with the conduct of a trade or business in the United States, and
                        </ListItem>
                        <ListItem>
                          The undersigned has examined the information on this form and to the best of
                          my knowledge and belief it is true, correct, and complete.
                        </ListItem>
                        `}
                      description="Legal information"
                      values={{
                        ListItem: (msg) => <ListItem>{msg}</ListItem>,
                      }}
                    />
                  </OrderedList>
                  <Text fontSize="md">
                    <FormattedMessage
                      defaultMessage="Furthermore, I authorize this form to be provided to any person that has control, receipt, or custody of the payment to which I am entitled or any person that can disburse or make the payments to which I am entitled."
                      description="Legal information"
                    />
                  </Text>
                </Stack>
              )}

              <TextFieldGroup
                name="taxInfo.signature"
                label={
                  isInUnitedStates
                    ? formatMessage({
                        defaultMessage: "Signature of U.S. person",
                        description: "Field input label",
                      })
                    : formatMessage({
                        defaultMessage: "Signature",
                        description: "Field input label",
                      })
                }
                placeholder={formatMessage({
                  defaultMessage: "Your full name",
                  description: "Field input placeholder",
                })}
                helperText={
                  <Text>
                    <FormattedMessage
                      defaultMessage="Typing your name serves as your signature and your consent to provide this data
                    in accordance with our <a>Privacy Policy</a>"
                      description="Legal form information"
                      values={{
                        a: (msg) => (
                          <a
                            style={{ textDecoration: "underline" }}
                            href={TILIA_TOS_URL}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {msg}
                          </a>
                        ),
                      }}
                    />
                  </Text>
                }
              />

              <Text as="i" fontSize="sm">
                <FormattedMessage
                  defaultMessage="By default, you are treated as an individual for tax purposes. To be treated as an entity, please visit tilia.io/support"
                  description="Legal tax information"
                />
              </Text>

              <NextButton section="taxInfo" onClick={goToNextSection} />
            </Stack>
          </AccordionSection>

          <AccordionSection
            title={formatMessage({
              defaultMessage: "Address",
              description: "Section title",
            })}
            section="contact"
            previousSection="personal"
          >
            <KYCFirstHalfAddressSection country={selectedCountry?.ISO} />
          </AccordionSection>
        </Accordion>

        <Button
          type="submit"
          isDisabled={!isValid || isSubmitting}
          isLoading={isSubmitting}
          loadingText={formatMessage({
            defaultMessage: "Hang tight...",
            description: "Loading message",
          })}
          size="lg"
          mt={6}
          mb={4}
          rightIcon={<ArrowForwardIcon />}
          width="full"
        >
          <FormattedMessage defaultMessage="Submit" description="Button label" />
        </Button>
        <FormValidationErrorDebugView validationErrors={errors} />
      </Box>
    </Form>
  );
}

export default memo(KYCFirstHalf);
